/**
 * Wizard names and robe colours for the landing page and roster (DESIGN
 * 6.3, 6.6 "random fun name if blank"). Names are "<First> the <Epithet>"
 * in the Magicka tradition and always fit MAX_NAME_LENGTH, which is why the
 * lists below are capped at 8 and 11 characters (8 + " the " + 11 = 24).
 */

import type { Rng } from './prng.js';
import { pick } from './prng.js';

/** The four robe colours of DESIGN 6.3, index = LobbyPlayer.color. */
export const PLAYER_COLORS: readonly string[] = ['#E04B4B', '#3C8BE0', '#45C26B', '#E0A33C'];

const COLOR_NAMES: readonly string[] = ['Red', 'Blue', 'Green', 'Gold'];

/** Any integer wraps into 0..3; garbage reads as 0. */
function colorIndex(i: number): number {
  const n = PLAYER_COLORS.length;
  return Number.isFinite(i) ? ((Math.floor(i) % n) + n) % n : 0;
}

/** Human name of a robe colour index. */
export function colorName(i: number): string {
  return COLOR_NAMES[colorIndex(i)] ?? 'Red';
}

/** Robe colour hex for an index; wraps like colorName. */
export function playerColorHex(i: number): string {
  return PLAYER_COLORS[colorIndex(i)] ?? '#E04B4B';
}

/** At most 8 characters each. */
export const WIZARD_FIRST_NAMES: readonly string[] = [
  'Vlad', 'Mortimer', 'Agatha', 'Zelda', 'Ignatius', 'Grimble', 'Thaddeus', 'Morwenna',
  'Ulric', 'Percival', 'Edwina', 'Fenwick', 'Osric', 'Beatrix', 'Dagny', 'Ezekiel',
  'Horatio', 'Isolde', 'Jasper', 'Kezia', 'Leopold', 'Minerva', 'Nigel', 'Ophelia',
  'Quentin', 'Rosalind', 'Septimus', 'Tabitha', 'Wilhelm', 'Ysolde', 'Casimir', 'Elspeth',
  'Gideon', 'Hespera', 'Ambrose', 'Bronwyn', 'Cedric', 'Drusilla', 'Eudora', 'Florian',
  'Gretchen', 'Harriet', 'Ivo', 'Juniper', 'Lorcan', 'Magnus', 'Nimue', 'Oswin',
  'Primrose', 'Rowena', 'Sigrid', 'Tobias', 'Ursula', 'Vesper', 'Wulfric', 'Xanthe',
  'Yorick', 'Zinnia', 'Barnaby', 'Clotilde', 'Dorian', 'Enid', 'Fabian', 'Godfrey',
];

/** At most 11 characters each; most are things that happen to you in this game. */
export const WIZARD_EPITHETS: readonly string[] = [
  'Damp', 'Soggy', 'Moist', 'Drizzled', 'Waterlogged', 'Slippery', 'Mildly Wet',
  'Singed', 'Crispy', 'Scorched', 'Flammable', 'Combustible', 'Smouldering', 'Parboiled',
  'Steamed', 'Lukewarm', 'Medium-Rare', 'Overheated', 'Explosive', 'Volatile',
  'Chilly', 'Frostbitten', 'Half-Frozen', 'Thawed', 'Brittle', 'Shivering',
  'Static', 'Electric', 'Conductive', 'Shocking', 'Thunderous', 'Sparkly',
  'Unready', 'Unstable', 'Unwise', 'Unlicensed', 'Unbothered', 'Untested',
  'Adequate', 'Accidental', 'Regrettable', 'Ill-Advised', 'Humble', 'Ominous',
  'Hasty', 'Dizzy', 'Reckless', 'Careful', 'Cautious', 'Bold', 'Brave-ish',
];

/**
 * A fun wizard name such as "Vlad the Damp". Uses only the injected rng so
 * the server can hand out names deterministically (and tests can pin them).
 */
export function randomWizardName(rng: Rng): string {
  return `${pick(rng, WIZARD_FIRST_NAMES)} the ${pick(rng, WIZARD_EPITHETS)}`;
}
